import { useState } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Info,
  X,
  TrendingUp,
  Loader2,
  Beef,
  Sparkles as SparklesIcon,
} from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import {
  useDailyMicronutrients,
  useNutrientSources,
  NutrientData,
  NutrientKey,
  NutrientSource,
} from '@/hooks/useMicronutrients';
import { fmtNum } from '@/utils/format';
import { NutrientsPeriod } from '@/components/NutrientsPeriod';
import {
  addDays, addMonths, endOfMonth, format, isSameMonth, isToday, isYesterday,
  parseISO, startOfMonth, subDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';

type CategoryKey = 'vitamins' | 'minerals' | 'fats' | 'profile';

interface Category {
  key: CategoryKey;
  label: string;
  emoji: string;
  nutrients: NutrientKey[];
}

const CATEGORIES: Category[] = [
  {
    key: 'profile',
    label: 'Пищевой профиль',
    emoji: '🍽',
    nutrients: ['fiber', 'vegetables_fruits'],
  },
  {
    key: 'minerals',
    label: 'Минералы',
    emoji: '💎',
    nutrients: ['calcium', 'iron', 'magnesium', 'zinc', 'potassium'],
  },
  {
    key: 'vitamins',
    label: 'Витамины',
    emoji: '☀️',
    nutrients: ['vitamin_d', 'vitamin_b12', 'vitamin_c'],
  },
  {
    key: 'fats',
    label: 'Жиры',
    emoji: '🐟',
    nutrients: ['omega3', 'saturated_fat'],
  },
];

type View = 'day' | 'week' | 'month' | 'custom';
const VIEWS: { key: View; label: string }[] = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'custom', label: 'Период' },
];
const iso = (d: Date) => format(d, 'yyyy-MM-dd');

export default function Micronutrients() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  // Ретроспектива (06.10.2026): раньше экран показывал только сегодня. Данные за
  // прошлые дни уже лежат в базе с момента записи еды, поэтому листать и складывать
  // их можно без единого запроса к модели.
  const [view, setView] = useState<View>('day');
  const [date, setDate] = useState(() => new Date());      // выбранный день
  const [anchor, setAnchor] = useState(() => new Date());  // конец недели / любой день месяца
  const today = new Date();
  const [customFrom, setCustomFrom] = useState(() => iso(subDays(new Date(), 29)));
  const [customTo, setCustomTo] = useState(() => iso(new Date()));

  const { nutrients, stats, loading } = useDailyMicronutrients(date);
  // Из каких продуктов рациона этого дня набрался каждый нутриент
  const { sources } = useNutrientSources(date);

  const [showInfoModal, setShowInfoModal] = useState(false);

  function handleBack() {
    haptic('light');
    nav(-1);
  }

  // Границы периода. Будущее не показываем: там только запланированная еда.
  const cap = (d: Date) => (d > today ? today : d);
  const range = (() => {
    if (view === 'week') {
      const to = cap(anchor);
      return { from: iso(subDays(to, 6)), to: iso(to) };
    }
    if (view === 'month') {
      return { from: iso(startOfMonth(anchor)), to: iso(cap(endOfMonth(anchor))) };
    }
    // Даты могли выбрать в любом порядке — раскладываем сами, а не ругаемся
    const [a, b] = customFrom <= customTo ? [customFrom, customTo] : [customTo, customFrom];
    return { from: a, to: b > iso(today) ? iso(today) : b };
  })();

  const atToday = view === 'day' ? isToday(date)
    : view === 'week' ? iso(cap(anchor)) === iso(today)
    : isSameMonth(anchor, today);

  function shift(dir: -1 | 1) {
    haptic('light');
    if (view === 'day') setDate((d) => cap(addDays(d, dir)));
    else if (view === 'week') setAnchor((d) => cap(addDays(cap(d), dir * 7)));
    else if (view === 'month') setAnchor((d) => cap(addMonths(d, dir)));
  }

  const navLabel = view === 'day'
    ? (isToday(date) ? 'Сегодня' : isYesterday(date) ? 'Вчера' : format(date, 'd MMMM, EEEEEE', { locale: ru }))
    : view === 'week'
    ? `${format(parseISO(range.from), 'd MMM', { locale: ru })} – ${format(parseISO(range.to), 'd MMM', { locale: ru })}`
    : format(anchor, 'LLLL yyyy', { locale: ru });

  // Группировка нутриентов по категориям
  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    items: cat.nutrients
      .map((key) => nutrients.find((n) => n.key === key))
      .filter(Boolean) as NutrientData[],
  })).filter((cat) => cat.items.length > 0);

  const dayEmpty = !nutrients.length || !nutrients.some((n) => n.consumed > 0);

  return (
    <div className="px-5 pt-4 pb-28 max-w-md mx-auto">
      <Header onBack={handleBack} onInfoClick={() => setShowInfoModal(true)} />

      {/* Что смотрим: один день или период */}
      <div className="flex gap-1 p-1 bg-bg-elevated rounded-xl mb-3">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => { haptic('light'); setView(v.key); }}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
              view === v.key ? 'bg-emerald-glow text-bg shadow-sm' : 'text-ink-muted'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Листание или выбор дат */}
      {view === 'custom' ? (
        <div className="flex items-center gap-2 mb-4">
          <input
            type="date"
            value={customFrom}
            max={iso(today)}
            onChange={(e) => e.target.value && setCustomFrom(e.target.value)}
            className="input flex-1 text-sm"
            aria-label="Начало периода"
          />
          <span className="text-ink-dim text-xs">—</span>
          <input
            type="date"
            value={customTo}
            max={iso(today)}
            onChange={(e) => e.target.value && setCustomTo(e.target.value)}
            className="input flex-1 text-sm"
            aria-label="Конец периода"
          />
        </div>
      ) : (
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => shift(-1)}
            className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
            aria-label="Раньше"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="font-display text-base first-letter:uppercase">{navLabel}</div>
          <button
            type="button"
            onClick={() => shift(1)}
            disabled={atToday}
            className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center disabled:opacity-30"
            aria-label="Позже"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {view !== 'day' ? (
        <NutrientsPeriod
          from={range.from}
          to={range.to}
          onPickDay={(d) => { haptic('light'); setDate(parseISO(d)); setView('day'); }}
        />
      ) : loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="animate-spin text-emerald-glow mb-3" />
          <div className="text-sm text-ink-dim">Считаю микронутриенты...</div>
        </div>
      ) : dayEmpty ? (
        <div className="card-elevated p-6 text-center mt-4">
          <div className="text-5xl mb-3">🥗</div>
          <div className="font-display font-bold text-lg mb-2">
            {isToday(date) ? 'Пока пусто' : 'За этот день записей нет'}
          </div>
          <p className="text-sm text-ink-dim leading-relaxed mb-4">
            {isToday(date)
              ? 'Запиши что-то — Дофи посчитает не только калории, но и 11 ключевых нутриентов из твоей еды.'
              : 'Полистай на соседние дни или открой неделю — там видно, когда записи были.'}
          </p>
          {isToday(date) && (
            <button
              type="button"
              onClick={() => {
                haptic('medium');
                nav('/');
              }}
              className="text-emerald-glow text-sm font-medium hover:underline"
            >
              На главный →
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Краткая сводка */}
          <SummaryBar stats={stats} />

          {/* Категории */}
          <div className="space-y-5 mt-5">
            {grouped.map((category) => (
              <CategorySection key={category.key} category={category} sources={sources} />
            ))}
          </div>
        </>
      )}

      {/* Дисклеймер */}
      <DisclaimerFooter onInfoClick={() => setShowInfoModal(true)} />

      {/* Модал с информацией */}
      <InfoModal open={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </div>
  );
}

// ============================================
// ШАПКА
// ============================================

function Header({ onBack, onInfoClick }: { onBack: () => void; onInfoClick: () => void }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <button
        type="button"
        onClick={onBack}
        className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <div className="flex-1">
        <h1 className="font-display text-xl font-bold">Микронутриенты</h1>
        <div className="text-[11px] text-ink-dim">По дням и за период</div>
      </div>
      <button
        type="button"
        onClick={onInfoClick}
        className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-dim hover:text-ink"
      >
        <Info size={18} />
      </button>
    </div>
  );
}

// ============================================
// СВОДКА
// ============================================

function SummaryBar({ stats }: { stats: ReturnType<typeof useDailyMicronutrients>['stats'] }) {
  return (
    <div className="card-elevated p-3 grid grid-cols-3 gap-2">
      <SummaryItem
        label="В норме"
        count={stats.good.length}
        total={stats.total}
        color="text-emerald-glow"
      />
      <SummaryItem
        label="Близко"
        count={stats.warning.length}
        total={stats.total}
        color="text-warn"
      />
      <SummaryItem
        label="Не хватает"
        count={stats.lack.length}
        total={stats.total}
        color="text-danger"
      />
    </div>
  );
}

function SummaryItem({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  return (
    <div className="text-center">
      <div className={`font-display text-xl font-bold ${color}`}>
        {count}
        <span className="text-[10px] text-ink-dim font-body ml-0.5">/{total}</span>
      </div>
      <div className="text-[10px] text-ink-dim uppercase tracking-wider">{label}</div>
    </div>
  );
}

// ============================================
// СЕКЦИЯ КАТЕГОРИИ
// ============================================

function CategorySection({
  category,
  sources,
}: {
  category: Category & { items: NutrientData[] };
  sources: Record<string, NutrientSource[]>;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2 px-1">
        <span className="text-sm">{category.emoji}</span>
        <h2 className="text-xs uppercase tracking-wider text-ink-dim font-medium">
          {category.label}
        </h2>
      </div>
      <div className="space-y-2">
        {category.items.map((nutrient) => (
          <NutrientCard key={nutrient.key} nutrient={nutrient} sources={sources[nutrient.key]} />
        ))}
      </div>
    </div>
  );
}

// ============================================
// КАРТОЧКА НУТРИЕНТА
// ============================================

function NutrientCard({
  nutrient,
  sources,
}: {
  nutrient: NutrientData;
  sources?: NutrientSource[];
}) {
  const isReverse = nutrient.target_type === 'reverse';
  const isRange = nutrient.target_type === 'range';

  // Цвета по статусу
  const colorClass = {
    good: 'text-emerald-glow',
    warning: 'text-warn',
    lack: 'text-danger',
  }[nutrient.status];

  const barColorClass = {
    good: 'bg-emerald-glow',
    warning: 'bg-warn',
    lack: 'bg-danger',
  }[nutrient.status];

  // Лейбл статуса
  const statusLabel = (() => {
    if (isReverse) {
      if (nutrient.status === 'good') return '✓ В норме';
      if (nutrient.status === 'warning') return 'Близко к лимиту';
      return 'Превышен лимит';
    }
    if (isRange && nutrient.target_max) {
      if (nutrient.status === 'good') return '✓ В оптимальной зоне';
      if (nutrient.status === 'warning') return 'Близко к минимуму';
      return 'Меньше минимума';
    }
    if (nutrient.status === 'good') return '✓ В норме';
    if (nutrient.status === 'warning') return 'Можно добрать';
    return 'Не хватает';
  })();

  // Текст нормы
  const targetText = (() => {
    if (isRange && nutrient.target_max) {
      return `${fmtNum(nutrient.target)}-${fmtNum(nutrient.target_max)} ${nutrient.unit}`;
    }
    if (isReverse) {
      return `до ${fmtNum(nutrient.target)} ${nutrient.unit}`;
    }
    return `${fmtNum(nutrient.target)} ${nutrient.unit}`;
  })();

  // Процент (для отображения)
  const displayPercent = Math.min(150, nutrient.percentage);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="card p-3"
    >
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-base shrink-0">{nutrient.emoji}</span>
          <span className="font-medium text-sm text-ink truncate">{nutrient.label}</span>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs font-display font-semibold">
            <span className={colorClass}>{fmtNum(nutrient.consumed)}</span>
            <span className="text-ink-dim"> / {targetText}</span>
          </div>
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="relative h-1.5 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
        <motion.div
          className={`h-full rounded-full ${barColorClass}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, displayPercent)}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
        {/* Метка минимума для range-типа */}
        {isRange && nutrient.target_max && nutrient.target > 0 && (
          <div
            className="absolute top-0 bottom-0 w-px bg-ink-dim/40"
            style={{ left: `${(nutrient.target / nutrient.target_max) * 100}%` }}
          />
        )}
      </div>

      {/* Статус */}
      <div className="flex items-center justify-between">
        <div className={`text-[10px] ${colorClass}`}>{statusLabel}</div>
        <div className="text-[10px] text-ink-dim">
          {isReverse ? `${displayPercent}% от лимита` : `${displayPercent}%`}
        </div>
      </div>

      {/* Откуда набралось — главные источники из сегодняшней еды.
          Для «обратных» нутриентов (насыщенные жиры) это ответ на вопрос
          «откуда столько», поэтому подпись другая.

          Не показываем, когда набралось меньше процента от нормы: строка
          «0 из 23 · набралось из: банан 100%» формально верна (0.1 г из
          банана — действительно весь вклад), но читается как бред.
          И не пишем долю у единственного источника — «100%» там пустой шум. */}
      {sources && sources.length > 0 && displayPercent >= 1 && (
        <div className="mt-1.5 pt-1.5 border-t border-line/60 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="text-[9px] text-ink-dim shrink-0">
            {isReverse ? 'Набралось из:' : 'Откуда:'}
          </span>
          {sources.map((s) => (
            <span key={s.name} className="text-[9px] text-ink-muted">
              <span className="max-w-[110px] inline-block truncate align-bottom">{s.name}</span>
              {sources.length > 1 && <span className="text-ink-dim"> {s.share}%</span>}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ============================================
// ДИСКЛЕЙМЕР
// ============================================

function DisclaimerFooter({ onInfoClick }: { onInfoClick: () => void }) {
  return (
    <div className="mt-6 p-3 bg-bg-card border border-line rounded-xl">
      <div className="flex items-start gap-2">
        <Info size={14} className="text-ink-dim shrink-0 mt-0.5" />
        <div className="flex-1 text-[11px] text-ink-dim leading-relaxed">
          Нормы по NIH, FDA, ВОЗ — адаптированы под твою калорийность и возраст.
          Это информационная оценка по составу продуктов, не диагностика.{' '}
          <button
            type="button"
            onClick={onInfoClick}
            className="text-emerald-glow hover:underline"
          >
            Подробнее →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// МОДАЛ С ИНФОРМАЦИЕЙ
// ============================================

function InfoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open);
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-bg rounded-t-3xl flex flex-col"
            style={{
              maxHeight: 'calc(100vh - 80px)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
              <div className="max-w-md mx-auto">
                <div className="w-12 h-1 bg-line rounded-full mx-auto mb-4" />

                <div className="flex items-start justify-between mb-4">
                  <h2 className="font-display text-xl font-bold">О микронутриентах</h2>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-muted"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-4 text-sm text-ink leading-relaxed">
                  <section>
                    <div className="flex items-center gap-2 mb-2">
                      <SparklesIcon size={14} className="text-emerald-glow" />
                      <h3 className="font-display font-bold text-base">Что мы считаем</h3>
                    </div>
                    <p className="text-ink-muted">
                      Slava Health анализирует 11 ключевых нутриентов из твоей еды по данным
                      USDA — клетчатку, витамины D, B12, C, минералы (кальций, железо, магний,
                      калий), омега-3 и насыщенные жиры.
                    </p>
                  </section>

                  <section>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={14} className="text-sky-glow" />
                      <h3 className="font-display font-bold text-base">
                        Персональные нормы
                      </h3>
                    </div>
                    <p className="text-ink-muted">
                      В отличие от других приложений, нормы у нас{' '}
                      <strong className="text-ink">не усреднённые</strong>. Они
                      адаптируются под твою калорийность, пол и возраст. Если ешь
                      больше — норма выше, ешь меньше — норма ниже. Это точнее.
                    </p>
                  </section>

                  <section>
                    <div className="flex items-center gap-2 mb-2">
                      <Beef size={14} className="text-warn" />
                      <h3 className="font-display font-bold text-base">Источники норм</h3>
                    </div>
                    <ul className="text-ink-muted space-y-1 text-xs">
                      <li>
                        • <strong className="text-ink">NIH</strong> — National Institutes
                        of Health (США)
                      </li>
                      <li>
                        • <strong className="text-ink">FDA</strong> — Food and Drug
                        Administration (США)
                      </li>
                      <li>
                        • <strong className="text-ink">ВОЗ</strong> — Всемирная организация
                        здравоохранения
                      </li>
                      <li>
                        • <strong className="text-ink">AHA</strong> — American Heart
                        Association
                      </li>
                    </ul>
                  </section>

                  <section className="bg-warn/5 border border-warn/30 rounded-xl p-3">
                    <h3 className="font-display font-bold text-sm text-warn mb-1">
                      ⚠️ Важно понимать
                    </h3>
                    <p className="text-xs text-ink-muted leading-relaxed">
                      Это <strong className="text-ink">информационная</strong> оценка по
                      составу продуктов. Точные значения зависят от множества факторов
                      (сорт, обработка, регион). Slava Health{' '}
                      <strong className="text-ink">не заменяет</strong> анализ крови или
                      консультацию врача.
                    </p>
                    <p className="text-xs text-ink-muted leading-relaxed mt-2">
                      При подозрении на реальный дефицит — обратись к врачу для анализов.
                    </p>
                  </section>

                  <section>
                    <h3 className="font-display font-bold text-base mb-2">
                      Цветовая шкала
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-emerald-glow shrink-0" />
                        <span>
                          <strong className="text-emerald-glow">В норме</strong> — 80%+
                          от рекомендуемой
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-warn shrink-0" />
                        <span>
                          <strong className="text-warn">Можно добрать</strong> — 50-80%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-danger shrink-0" />
                        <span>
                          <strong className="text-danger">Не хватает</strong> — менее 50%
                        </span>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <div className="shrink-0 px-5 pt-3 pb-5 border-t border-line/30 bg-bg">
              <div className="max-w-md mx-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-medium text-sm"
                >
                  Понятно
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
